import { Field } from "../../design-system";
export function previewManuscript(source: string, fileNames: string[]) {
  let body = source.trim(),
    visibility = "전체",
    tags: string[] = [],
    category = "기본 카테고리";
  const errors: string[] = [];
  const head = body.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/);
  if (
    head &&
    head[1]
      .split(/\r?\n/)
      .filter(Boolean)
      .every((l) => l.includes(":"))
  ) {
    for (const line of head[1].split(/\r?\n/)) {
      const split = line.indexOf(":");
      const key = line.slice(0, split).trim(),
        val = line.slice(split + 1).trim();
      if (key === "공개" && val) visibility = val;
      if (key === "태그")
        tags = val.includes("#")
          ? val
              .split("#")
              .map((t) => t.trim())
              .filter(Boolean)
          : val
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean);
      if (key === "카테고리" && val) category = val;
    }
    body = body.slice(head[0].length);
  }
  const title = body.match(/^# (.+)$/m)?.[1].trim() ?? "";
  if (!title) errors.push("제목이 없습니다. # 제목 줄을 넣어 주세요.");
  if (title.length > 100) errors.push("제목은 100자까지 입력할 수 있습니다.");
  if (!["전체", "이웃", "비공개"].includes(visibility))
    errors.push("공개 범위는 전체·이웃·비공개 중 하나로 입력해 주세요.");
  if (tags.length > 30) errors.push("태그는 최대 30개입니다.");
  const cleaned = body.replace(/<!--[^]*?-->/g, "").replace(/^# .+$/m, "");
  const blocks = cleaned
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  const paragraphs = blocks.filter(
    (b) => !/^(!\[|#{2,}|>|---|\[.*\]\(https?:)/.test(b),
  );
  if (!paragraphs.length) errors.push("본문 문단을 하나 이상 입력해 주세요.");
  const media = [...cleaned.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g)].map((m) =>
    m[1].trim(),
  );
  const unique = [...new Set(media.map((f) => f.toLowerCase()))];
  const attached = fileNames.map((f) => f.toLowerCase());
  unique.forEach((f) => {
    if (!attached.includes(f))
      errors.push(`원고의 ${f} 파일이 첨부에 없습니다.`);
  });
  attached.forEach((f) => {
    if (!unique.includes(f)) errors.push(`첨부 ${f}은 원고 어디에도 없습니다.`);
  });
  if (media.filter((f) => /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(f)).length > 10)
    errors.push("영상은 글 하나에 최대 10개입니다.");
  for (const line of cleaned.split("\n")) {
    if (
      [...line.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g)].filter(
        (m) => !/\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(m[1]),
      ).length > 10
    )
      errors.push("한 자리 사진 묶음은 최대 10장입니다.");
  }
  return {
    title,
    visibility,
    tags,
    category,
    paragraphs: paragraphs.length,
    media: unique,
    blocks,
    errors,
  };
}
export function ManuscriptEditor({
  value,
  onChange,
  files,
  onFiles,
  successMessage = "형식과 첨부 파일이 일치합니다. 등록하면 승인된 원고로 실행합니다.",
}: {
  value: string;
  onChange: (v: string) => void;
  files: string[];
  onFiles: (v: string[]) => void;
  successMessage?: string;
}) {
  const preview = previewManuscript(value, files);
  return (
    <div className="lwd-stack">
      <div className="lwd-actions">
        <a href="/meta-crew-manuscript/원고-양식.md" download>
          빈 원고 양식
        </a>
        <a href="/meta-crew-manuscript/원고-작성-가이드.md" download>
          작성 가이드
        </a>
      </div>
      <Field
        label="원고 파일 불러오기"
        type="file"
        accept=".md,text/markdown"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) onChange(await file.text());
        }}
      />
      <label className="field">
        <span>원고</span>
        <textarea
          aria-label="원고"
          className="input lwd-manuscript"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            "---\n태그: 진료 안내\n공개: 전체\n---\n# 글 제목\n\n완성된 본문을 붙여넣으세요."
          }
          required
        />
      </label>
      <Field
        label="사진·영상 첨부"
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) =>
          onFiles(Array.from(e.target.files ?? []).map((f) => f.name))
        }
      />
      {!!files.length && <p className="meta-crew-muted">{files.join(" · ")}</p>}
      <section className="lwd-preview" aria-label="원고 확인">
        <strong>{preview.title || "원고 확인"}</strong>
        <p>
          {preview.visibility} 공개 · 문단 {preview.paragraphs}개 · 파일{" "}
          {preview.media.length}개 · {preview.category}
        </p>
        <p>태그: {preview.tags.join(", ") || "없음"}</p>
        {preview.errors.length > 0 ? (
          <ul className="lwd-errors" role="alert">
            {preview.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : (
          <p role="status">{successMessage}</p>
        )}
        <details>
          <summary>본문 순서 미리보기</summary>
          <ol>
            {preview.blocks.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ol>
        </details>
      </section>
    </div>
  );
}
